import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion'; // Using Framer Motion for unique animations
import Notification from '../../Component/Notification';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  
  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false, message: '', type: 'success'
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // const response = await fetch('http://localhost:2025/api/admin/login', {
      const response = await fetch('https://www.projectinfotech.in/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('adminToken', data.token);
        setNotify({ isOpen: true, message: 'Access Granted. Redirecting...', type: 'success' });
        setTimeout(() => navigate('/admin/dashboard'), 1000);
      } else {
        setNotify({ isOpen: true, message: 'Access Denied: Invalid Credentials', type: 'error' });
      }
    } catch (error) {
      setNotify({ isOpen: true, message: 'Server Unreachable', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-900 relative overflow-hidden">
      
      <Notification 
        isOpen={notify.isOpen} 
        message={notify.message} 
        type={notify.type} 
        onClose={() => setNotify({ ...notify, isOpen: false })} 
      />

      {/* --- Animated Background Elements --- */}
      {/* Moving Red Gradient Blob */}
      <motion.div 
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          opacity: [0.3, 0.5, 0.3] 
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-20 -left-20 w-96 h-96 bg-red-600 rounded-full blur-[120px] opacity-30"
      />
      
      {/* Moving Blue/Dark Gradient Blob */}
      <motion.div 
        animate={{ 
          scale: [1, 1.1, 1],
          x: [0, 50, 0],
          opacity: [0.2, 0.4, 0.2]
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -bottom-20 -right-20 w-[500px] h-[500px] bg-blue-900 rounded-full blur-[140px] opacity-30"
      />

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 z-0"></div>

      {/* --- Main Login Card (Glassmorphism) --- */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ 
          duration: 0.6, 
          ease: [0.22, 1, 0.36, 1] // Custom spring-like easing
        }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-gray-900/60 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl overflow-hidden">
          
          {/* Header Section */}
          <div className="relative h-32 bg-gradient-to-r from-red-900 to-gray-900 flex flex-col items-center justify-center">
             <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-30"></div>
             
             <motion.div 
               initial={{ y: -20, opacity: 0 }}
               animate={{ y: 0, opacity: 1 }}
               transition={{ delay: 0.2 }}
               className="relative z-10 bg-gray-900/50 p-3 rounded-full border border-red-500/30 shadow-lg backdrop-blur-sm"
             >
                <ShieldCheck className="text-red-500 w-10 h-10" />
             </motion.div>
             
             <motion.h2 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               transition={{ delay: 0.3 }}
               className="relative z-10 text-white font-bold text-xl mt-3 tracking-widest uppercase"
             >
               PIT <span className="text-red-500">Control</span>
             </motion.h2>
          </div>

          {/* Form Section */}
          <div className="p-8 pt-10">
            <form onSubmit={handleLogin} className="space-y-6">
              
              {/* Username Input */}
              <motion.div 
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="group relative"
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-500 group-focus-within:text-red-500 transition-colors" />
                </div>
                <input
                  type="text"
                  name="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-700 rounded-lg leading-5 bg-gray-800/50 text-gray-100 placeholder-gray-500 focus:outline-none focus:bg-gray-800 focus:border-red-500 focus:ring-1 focus:ring-red-500 transition duration-200 sm:text-sm"
                  placeholder="Admin Username"
                />
              </motion.div>

              {/* Password Input */}
              <motion.div 
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="group relative"
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-500 group-focus-within:text-red-500 transition-colors" />
                </div>
                <input
                  type="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-700 rounded-lg leading-5 bg-gray-800/50 text-gray-100 placeholder-gray-500 focus:outline-none focus:bg-gray-800 focus:border-red-500 focus:ring-1 focus:ring-red-500 transition duration-200 sm:text-sm"
                  placeholder="Secret Key"
                />
              </motion.div>

              {/* Action Button */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-lg text-white bg-gradient-to-r from-red-700 to-red-900 hover:from-red-600 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-lg transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                       <Loader2 className="animate-spin h-5 w-5" /> Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                       Authenticate <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </button>
              </motion.div>

            </form>

            <div className="mt-8 text-center">
              <a href="/" className="text-xs text-gray-500 hover:text-red-400 transition-colors">
                Return to Public Portal
              </a>
            </div>
          </div>
        </div>
        
        {/* Shadow Glow under the card */}
        <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-blue-600 rounded-2xl blur opacity-20 -z-10 transition duration-1000 group-hover:opacity-100"></div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;